import { Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Workspace } from "@/hooks/useWorkspace";

type WorkspaceChipsProps = {
  workspaces: Workspace[];
  /** Currently selected workspace IDs. Empty array means "All workspaces". */
  selectedIds: string[];
  onToggle: (workspaceId: string) => void;
  onClear: () => void;
  label?: string;
};

const labelFor = (w: Workspace) => w.workspace_label || w.display_name || "Workspace";

/**
 * Top-level workspace filter chips for My Library and My Excerpts.
 * Renders a row of toggle chips: an "All workspaces" pill plus one
 * per accessible workspace. Hidden by the parent when the user only
 * has a single workspace (mode === "single") or when the workspace
 * is in isolated mode.
 */
export const WorkspaceChips = ({
  workspaces,
  selectedIds,
  onToggle,
  onClear,
  label = "Workspaces",
}: WorkspaceChipsProps) => {
  if (workspaces.length <= 1) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Layers className="h-4 w-4 text-primary" /> {label}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant={selectedIds.length === 0 ? "secondary" : "outline"}
          size="sm"
          onClick={onClear}
        >
          All workspaces
        </Button>
        {workspaces.map((w) => (
          <Button
            key={w.id}
            type="button"
            variant={selectedIds.includes(w.id) ? "secondary" : "outline"}
            size="sm"
            onClick={() => onToggle(w.id)}
          >
            {labelFor(w)}
          </Button>
        ))}
      </div>
    </div>
  );
};
